import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bell, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { openChat } from '@/components/ChatButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const relativeTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'hace instantes';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
};

const NotificationBell = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const lastSeenIdRef = React.useRef(null);
  const audioCtxRef = React.useRef(null);

  const playAlert = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        // Some browsers require a user gesture before creating an AudioContext.
        // The bell polls silently; we only try to instantiate on demand and
        // fail gracefully if not allowed.
        // eslint-disable-next-line no-undef
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') { ctx.resume(); }
      const now = ctx.currentTime;
      // Two short "ding" tones so the seller notices even in noisy places.
      [880, 1320].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.001, now + i * 0.28);
        gain.gain.exponentialRampToValueAtTime(0.3, now + i * 0.28 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.28 + 0.25);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + i * 0.28);
        osc.stop(now + i * 0.28 + 0.26);
      });
    } catch (_) { /* silent */ }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { headers: { Authorization: getAuthHeader() } });
      const list = res.data.items || [];
      // Detect NEW sos_auction notifications since last poll and play alert.
      if (lastSeenIdRef.current) {
        const idx = list.findIndex((n) => n.id === lastSeenIdRef.current);
        const fresh = idx === -1 ? list : list.slice(0, idx);
        const soundNotif = fresh.find((n) => n.sound || n.type === 'sos_auction' || n.type === 'bid_accepted');
        if (soundNotif) {
          playAlert();
          toast.warning(soundNotif.message, { duration: 6000 });
        }
      }
      if (list[0]) lastSeenIdRef.current = list[0].id;
      setItems(list);
      setUnread(res.data.unread || 0);
    } catch (_) { /* silent */ }
  }, [getAuthHeader, playAlert]);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 15000);
    return () => clearInterval(t);
  }, [fetchData]);

  const markRead = async () => {
    if (unread === 0) return;
    try {
      await axios.post(`${API}/notifications/mark-read`, {}, { headers: { Authorization: getAuthHeader() } });
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (_) { /* silent */ }
  };

  const handleClick = (n) => {
    setOpen(false);
    const link = n.link || (n.match_id ? `/dashboard?tab=matches&match=${n.match_id}` : null);
    if (!link) return;
    const url = new URL(link, window.location.origin);
    const chatId = url.searchParams.get('chat');
    if (chatId) {
      openChat({ conversation_id: chatId });
      return;
    }
    // Same path: only the query changes → dashboards listen to location.search
    navigate(`${url.pathname}${url.search}`);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) markRead(); }}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid="notification-bell"
          className="relative text-slate-600 hover:text-slate-900"
        >
          <Bell size={20} />
          {unread > 0 && (
            <span
              data-testid="notification-unread-count"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-list">
        <div className="p-3 border-b border-slate-100">
          <p className="text-sm font-semibold text-slate-900">Notificaciones</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="p-4 text-sm text-slate-500 text-center">Sin notificaciones</p>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                type="button"
                data-testid={`notification-item-${n.id}`}
                onClick={() => handleClick(n)}
                className={`w-full text-left p-3 border-b border-slate-50 last:border-b-0 text-sm flex items-start gap-2 hover:bg-slate-50 transition-colors ${n.read ? 'text-slate-600' : 'text-slate-900 bg-emerald-50/40'}`}
              >
                <div className="flex-1 min-w-0">
                  {n.from_name && (
                    <p className="text-xs font-bold text-emerald-700 truncate" data-testid={`notification-from-${n.id}`}>{n.from_name}</p>
                  )}
                  <p className="leading-snug">{n.message}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{relativeTime(n.created_at)}</p>
                </div>
                {(n.link || n.match_id) && <ChevronRight size={14} className="text-slate-300 mt-1 flex-shrink-0" />}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
